import type { BaseModel } from './baseModel';
import type { Message } from './message';
import type { ScheduledMessage } from './scheduledMessage';

// TODO: delete this
export type ClientStatus = 'open' | 'pending' | 'success-closed' | 'failed-closed';

interface CustomAttributes {
  [key: string]: string | number | boolean | undefined;
}

export interface Address {
  street?: string | null;
  number?: string | null;
  homeNumber?: string | null;
  commune?: string | null;
  region?: string | null;
  regionId?: string | null;
}

export interface ClientDraft {
  createdAt: Date;
  updatedAt: Date;
  firstName: string;
  lastName: string;
  email: string;
  rut: string | null;
  dni: string | null;
  isTestClient: boolean | null;
  isBlocked?: boolean | null;
  // Clients from Meta's WhatsApp username rollout have no phone; `whatsAppUserId` is their address.
  phone: string | null;
  whatsAppUserId?: string | null;
  // Username the client picked on WhatsApp. Display only — it cannot be messaged and can change.
  whatsAppUsername?: string | null;
  chatType: string | null;
  savingsAmount: number | null;
  vehicleValue?: number | null;
  housingStatus?: string | null;
  educationalLevel?: string | null;
  degree?: string | null;
  companyName?: string | null;
  notes?: string | null;
  adminNotes?: string | null;
  workContract?: string | null;
  businessUnitId: number;
  motherLastName?: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
  maritalStatus: string | null;
  address?: Address | string | null;
  bankAccount?: {
    bankName?: string | null;
    accountType?: 'VISTA' | 'CORRIENTE' | 'RUT' | null;
    accountNumber?: string | null;
  } | null;
  salary: number;
  salarySupplement?: number | null;
  totalSalary: number;
  score: number; // score given by the system (0-100), based on financialScore and interestScore
  financialScore: number | null;
  interestScore: number | null;
  manualScore: number | null; // used if we want to override the system score
  disabledAutomaticResponses: boolean | null;
  source: string | null;
  lastMessageDate: Date;
  clientLastMessageDate: Date | null;
  lastMessageText: string | null;
  messagesCount: number;
  clientMessagesCount: number;
  imagesSent: number;
  followUpsSent: number;
  floidRequestsCount: number;
  floidRequestsDetails?: string;
  status?: ClientStatus | null;
  didBookVisit?: boolean;
  didVisit?: boolean;
  userScore?: number; // score given by the seller (0-10) to show how good of a prospect the client is
  webhookBodyAtCreation?: string | null;
  hubspotId?: number | null;
  hubspotDealId?: string | null;
  externalId?: string | null;
  prospectMessage?: string | null;
  messages?: Message[];
  hasVehicleAsPartPayment: boolean;
  requiresFinancing: boolean;
  monthlyPaymentsAmount: number | null;
  budgetAmount: number | null;
  modelsOfInterest: string[] | null;
  modelsOfInterestIds: number[] | null;
  brandOfInterest?: string | null;
  hubspotComment?: string | null;
  disabledFollowUps?: boolean;
  hasValidName?: boolean;
  lastCRMUpdate?: Date | null;
  visitDate?: Date | null;
  isClosedConversation?: boolean;
  cost?: number | null;
  topics?: string[] | null;
  endReason?: string | null;
  branchOffice?: string | null;
  businessUnit?: {
    id: number;
    name: string;
    type: string;
    hasSellerManagement: boolean;
    phoneNumber?: string;
  };
  hasValidatedAllData?: boolean;
  aiSafetyStatus?: string | null;
  creditSimulations?: unknown[];
  surveys?: {
    id: number;
    name: string;
    results: {
      question: string;
      response?: string | null;
    }[];
    status: 'pending' | 'finished' | 'aborted';
    updatedAt: Date;
  }[];
  creditStatus?: string | null;
  customAttributes?: CustomAttributes | null;
  userId?: number | null;
  user?: {
    id: number;
    name?: string | null;
    email?: string | null;
  };
  publicChatLink?: string | null;
  lastSentMessageRole?: string | null;
  unreadMessagesCount: number;
  clientType?: 'buy' | 'sell' | 'after-sale' | null;
  pilotAssignedUser?: string | null;
  lastLeadStatus?: string | null;
  privacyPolicyAccepted?: boolean | null;
  hasCallPermissionNow?: boolean;
  chatSummary?: string;
  outboundCampaignId?: string | null;
  sales?: Array<{
    id: number;
    insuranceType: 'loanProtectionInsurance' | 'unemploymentInsurance';
    advancePercentage: number | null;
  }>;
  // Populated only by the "Pendientes CRM" view (filtered by crmStage): the matching Sale's
  // Salesforce details — see getFilteredClients in saas_back clientHelpers.
  crmSaleExternalId?: string | null;
  /** Insurex's readable opportunity number; `crmSaleExternalId` is the opaque Salesforce record Id. */
  crmSaleOpportunityNumber?: string | null;
  crmSaleInsuranceType?: 'loanProtectionInsurance' | 'unemploymentInsurance' | null;
  crmSaleSubStage?: string | null;
  crmSaleLastUpdate?: string | null;
}

export interface Client extends BaseModel, ClientDraft {
  scheduledMessages?: ScheduledMessage[];
}

export interface EditableClientAttributes {
  status?: ClientStatus | null;
  disabledAutomaticResponses?: boolean;
  userId?: number | null;
  notes?: string | null;
  topics?: string[] | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  rut?: string | null;
  dni?: string | null;
  gender?: string | null;
  nationality?: string | null;
  dateOfBirth?: string | null;
  address?: Address | null;
}

export interface EditableAdminClientAttributes extends EditableClientAttributes {
  adminNotes?: string;
  rut?: string;
  dni?: string;
  email?: string;
  phone?: number;
  firstName?: string;
  lastName?: string;
  salary?: number;
  salarySupplement?: number;
  savingsAmount?: number;
  vehicleValue?: number;
  housingStatus?: string;
  educationLevel?: string;
  degree?: string;
  workContract?: string;
  motherLastName?: string;
  gender?: string;
  dateOfBirth?: string;
  nationality?: string;
  maritalStatus?: string;
  source?: string;
  outboundCampaignId?: string;
  manualScore?: number;
  disabledAutomaticResponses?: boolean;
  disabledFollowUps?: boolean;
  isTestClient?: boolean;
  prospectMessage?: string;
  subProjectId?: number;
  hasVehicleAsPartPayment?: boolean;
  requiresFinancing?: boolean;
  monthlyPaymentsAmount?: number;
  budgetAmount?: number;
  modelsOfInterest?: string[];
  brandOfInterest?: string;
  hasValidName?: boolean;
  branchOffice?: string;
  aiSafetyStatus?: string;
  externalId?: string;
  topics?: string[] | null;
  address?: Address | null;
}

export interface ClientWithLastMessage {
  id: number;
  lastName: string;
  firstName: string;
  phone: string;
  businessUnitName: string | null;
  messageId: number;
  messageText: string;
  messageStatus: string;
  createdAt: Date;
}

export const clientAttributes = [
  { title: 'nombre', value: 'client.firstName' },
  { title: 'apellido', value: 'client.lastName' },
  { title: 'sucursal', value: 'client.branchOffice' },
  { title: 'marca de interés', value: 'client.brandOfInterest' },
  { title: 'modelo de interés', value: 'client.modelsOfInterest' },
  { title: 'nombre ejecutivo', value: 'lead.externalLeadOwner' },
];

// Attributes of the vehicle the client already owns. They carry a `||` fallback because a client
// without a ClientCar row would otherwise resolve to an empty string, and Meta rejects empty
// template parameters at send time.
export const ownedCarAttributes = [
  { title: 'auto del cliente (marca y modelo)', value: 'ownedCar.label||tu vehículo' },
  { title: 'marca del auto', value: 'ownedCar.brand||tu vehículo' },
  { title: 'modelo del auto', value: 'ownedCar.model||tu vehículo' },
  { title: 'año del auto', value: 'ownedCar.year' },
  { title: 'patente del auto', value: 'ownedCar.licensePlate||tu vehículo' },
  { title: 'fecha de compra del auto', value: 'dateOf.ownedCar.purchaseDate||sin fecha' },
];

export const getTemplateAttributesForBusinessType = (
  businessUnitType?: string | null
): { title: string; value: string }[] =>
  businessUnitType === 'after_sales' ? [...clientAttributes, ...ownedCarAttributes] : clientAttributes;

// Maps a template body parameter value (clientAttributes[].value) to its CSV mapping attribute key
// (the keys used in MapCsvHeaders). Lets the campaign builder force a CSV column for every attribute
// referenced by the selected template. Attributes with no CSV-mappable column (e.g. amount) are omitted.
export const templateAttributeToCsvKey: Record<string, string | string[]> = {
  'client.firstName': 'firstName',
  'client.lastName': 'lastName',
  'client.branchOffice': 'branchOffice',
  'client.brandOfInterest': 'brandOfInterest',
  'client.modelsOfInterest': 'model',
  'lead.externalLeadOwner': 'externalLeadOwner',
  'ownedCar.label': ['ownedCarBrand', 'ownedCarModel'],
  'ownedCar.brand': 'ownedCarBrand',
  'ownedCar.model': 'ownedCarModel',
  'ownedCar.year': 'ownedCarYear',
  'ownedCar.vin': 'ownedCarVin',
  'ownedCar.licensePlate': 'ownedCarLicensePlate',
  'dateOf.ownedCar.purchaseDate': 'ownedCarPurchaseDate',
};
